// ============================================================================
// Project Work — detail view with inline edit + billing pipeline controls.
// 2026-05-22 (project_work_pipeline_stages migration).
//
// Editable fields: the service line (service_code + scope), notes, bill_amount, pay_amount,
// start_date, due_date, completion_date, assigned_partner_id.
//
// Stage section: shows current billing stage with role-gated advancement
// buttons. AM advances Draft → Ready_to_Invoice (requires bill+pay).
// Finance / superuser / executive / national_am advances
// Ready_to_Invoice → Invoiced and Invoiced → Completed.
// ============================================================================

import { canResubmitAnyProject, hasFullAmAuthority, showsSendBackToAm, worksProjectFinance } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FinanceHandoffStrip } from './finance/handoff-strip';
import { ArrowLeft, Loader2, User, UserPlus, AlertTriangle, CopyPlus, CornerUpLeft, Shuffle, X, Trash2 } from 'lucide-react';
import {
  Button,
  ErrorState,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { toNumOrNull } from '../../shared/coerce';
import { CopyReference } from '../../shared/copy-reference';
import {
  addProjectNote,
  assignFacilityAm,
  deleteProject,
  fetchProjectPayment,
  resolveAwaitingAmInput,
  sendProjectBackToAm,
  updateProject,
  updateProjectStage,
  useAmEligibleEmployees,
  useEmployeeNames,
  useProject,
  useProjectNotes,
  type ProjectPayment,
  repeatProject,
  saveProjectServiceLine,
  deleteProjectServiceLine,
  useProjectLinePayments,
  mintProjectServiceLinePayment,
  type ProjectLinePayment,
} from './hooks/use-project-data';
import {
  blankLine,
  deriveAmounts,
  deriveDates,
  lineIsSaveable,
  type EntryLine,
} from './lib/entry-derive';
import { diffLines, entryLineToInput, serviceLineToEntryLine } from './lib/line-edit';
import { ServiceLineCard } from './entry/service-line-card';
import {
  OTHER_SERVICE_CODE,
  PROJECT_EVENT_CLASS,
  useServiceCatalog,
} from './hooks/use-service-catalog';
import { rpcErrorMessage } from '../../shared/rpc-error';
import {
  PROJECT_STAGE_CLASSES,
  PROJECT_STAGE_LABELS,
  type ProjectRow,
  type ProjectStage,
  type UpdateProjectInput,
  type Facility,
} from '../../shared/types';
import { supabase } from '../../shared/supabase';
import { SmartSelect } from '../../components/smart-select';
import FacilityPicker from '../supply-management/facility-picker';
import { RouteToPanel, RecordActivityLog } from '../routing';
import { projectServiceLines } from './lib/project-service-label';
import { readyGate, rowToEditState, type EditState } from './lib/ready-gate';
// ⛔ The advance gate — condition AND wording — lives only in lib/advance-gate.ts.
// advance-gate.lockstep.test.ts fails if either is restated on this page.
import { canAdvance, getAdvanceBlockers, isNotSent } from './lib/advance-gate';
import { enteredClause, ownerCaveat, ownerClause } from './lib/attribution';
// Rung 2 of Ben's ladder — FORM → HELPER → TEXT. The card's "Walk me through it"
// is the SECOND door into the same component; the entry screen is the first.
import { ClassifyHelperDialog, type HelperOutcome } from './entry/classify-helper-dialog';
import { UNCLASSIFIED_EVENT_CLASS } from './lib/classify-helper';
// 2A — changing what a record IS after Finance has seen it. The rules (and the
// finding that reclassifying moves no money) live in lib/reclassify.ts.
import { reclassifyBlockers, reclassifyPatch } from './lib/reclassify';
import { useProjectAttribution } from './hooks/use-project-attribution';
import { errorMessage } from '../../shared/errors';
import { fmtCurrency } from './lib/fmt-currency';
import { FinancePanel, fmtPct, marginColorClass } from './finance-panel';
import { fmtDate } from './lib/fmt-date';
import { summariseLinePayments, canReadPaymentRecords } from './lib/line-owned-payment';

// AFB-0009 — who may bounce a card back to the AM (stage → Awaiting_AM_Input).
// Mirrors the prompt: finance, executive, superuser only — NOT AM/national_am/HR.
// (RLS currently only lets executive/superuser/national_am actually write this
// stage; the `finance` write is blocked by the finance_mark_invoiced WITH CHECK.
// The button is shown to finance per spec; the error surfaces gracefully and
// the RLS gap is a flagged follow-up — see the round-2 cowork report.)

// AFB-0082, 2026-08-19 — what a user reads instead of a raw 23514.
//
// `projects_reason_note_required_off_project` requires a note on every row whose
// event_class is not 'Project'. Jack hit that constraint as its own SQLSTATE
// text, on a screen that did not show him the field it was talking about.
const OFF_PROJECT_NOTE_REQUIRED =
  "This record is not ordinary billable work, so it needs a note saying what happened.";

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

// Form state shape (EditState) and rowToEditState now live in lib/ready-gate.ts,
// shared with Ask AI's project card (item #944).
const parseAmount = (s: string): number | null => toNumOrNull(s);

export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const { data, isLoading, error, refetch } = useProject(projectId ?? null);
  const { employee, profile } = useAuth();
  const navigate = useNavigate();
  const { employees: amEligible } = useAmEligibleEmployees();

  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Run C Phase 2 — every service line, editable in place. `original` is the
  // list as loaded, so a save can write only what actually changed instead of
  // rewriting three rows because one of them was touched.
  const [lines, setLines] = useState<EntryLine[]>([]);
  const [originalLines, setOriginalLines] = useState<EntryLine[]>([]);
  const [removeLine, setRemoveLine] = useState<EntryLine | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Run C Phase 4 — per-line payment. `payVersion` re-reads the preview after a
  // mint without reloading the whole project.
  const [payVersion, setPayVersion] = useState(0);
  const { rows: linePayments, error: linePaymentsError } = useProjectLinePayments(
    projectId ?? null,
    payVersion,
  );
  const [payBusy, setPayBusy] = useState<string | null>(null);
  const [payError, setPayError] = useState<Record<string, string>>({});
  const [stageError, setStageError] = useState<string | null>(null);
  const [stageBusy, setStageBusy] = useState<ProjectStage | null>(null);
  const [stageToast, setStageToast] = useState<string | null>(null);

  // AFB-0009 — send-back-to-AM modal state.
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackReason, setSendBackReason] = useState('');
  const [sendBackBusy, setSendBackBusy] = useState(false);
  const [resubmitBusy, setResubmitBusy] = useState(false);

  // AFB-0022 #1 — delete confirm modal state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // AFB-0022 #4 — assign-AM panel state.
  const [assignEid, setAssignEid] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Task 1 — bump to refresh the record activity log after a new route.
  const [routingVersion, setRoutingVersion] = useState(0);

  // 2026-06-04 — toggle the AM editor when an AM is already set.
  const [amEditOpen, setAmEditOpen] = useState(false);

  // The payment record for the WHOLE project, if one exists — a historical
  // project-level payment or a linked AP-report row (item #680). Read for the
  // For finance "Lines paid" row and for the delete warning.
  const [payment, setPayment] = useState<ProjectPayment | null>(null);

  // 2026-07-20 — link/relink this project to a facility (+ derived account).
  const [facilityEditOpen, setFacilityEditOpen] = useState(false);
  const [facilityBusy, setFacilityBusy] = useState(false);
  const [facilityError, setFacilityError] = useState<string | null>(null);

  // Re-seed edit state whenever a fresh project loads.
  useEffect(() => {
    if (data) {
      setEdit(rowToEditState(data));
      const seeded = projectServiceLines(data).map((row, i) =>
        serviceLineToEntryLine(row, `saved-${row.service_line_id}-${i}`),
      );
      setLines(seeded);
      setOriginalLines(seeded);
      setSaveError(null);
    }
  }, [data?.project_id, data?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  // AFB-0021 — load the linked sub payment (refresh when the project changes).
  useEffect(() => {
    if (!projectId) { setPayment(null); return; }
    let cancelled = false;
    fetchProjectPayment(projectId)
      .then((p) => { if (!cancelled) setPayment(p); })
      .catch(() => { /* RLS may hide it from non-finance; chip degrades to "none" */ });
    return () => { cancelled = true; };
  }, [projectId, data?.updated_at]);

  const role = profile?.role ?? null;
  // Finance-capable users (incl. extra_roles 'finance' hat holders like
  // Antonela) OR the existing primary-role set (which adds national_am).
  // Mirrors the projects_finance_update_all RLS, now on has_finance_capability().
  const canFinance = worksProjectFinance(profile);
  const stage = (data?.stage as ProjectStage | null) ?? null;

  // AFB-0009 — role gating for the bounce-back workflow.
  const canSendBack = showsSendBackToAm(role);
  // The AM who can resubmit is the AM-on-facility for this project, plus
  // national_am / superuser who can act across facilities. EID is the source
  // of truth (never the denormalized account_manager_name).
  const isAmOnFacility =
    !!employee?.employee_id &&
    data?.facilities?.account_manager_eid === employee.employee_id;
  const canResubmit = isAmOnFacility || canResubmitAnyProject(role);

  // AFB-0022 — full AM authority = superuser/executive/national_am (mirrors the
  // DB's has_full_am_authority()). Drives delete-any (#1) and assign-AM (#4).
  const hasFullAuthority = hasFullAmAuthority(role);
  const isCreator =
    !!employee?.employee_id && data?.created_by_eid === employee.employee_id;
  // #1 — creator deletes their own project; full authority deletes any.
  const canDelete = isCreator || hasFullAuthority;
  // 2026-06-04 — anyone setting up a not-yet-final facility may add/edit the AM;
  // once the facility is Active (final) changing it requires full AM authority.
  const facilityIsFinal = (data?.facilities?.status ?? null) === 'Active';
  const canAssignAm = facilityIsFinal ? hasFullAuthority : !!employee?.employee_id;
  const currentAm = data?.facilities?.account_manager ?? null;
  const currentAmName = currentAm
    ? `${currentAm.employee_name_first ?? ''} ${currentAm.employee_name_last ?? ''}`.trim()
    : '';
  const facilityMissingAm = !!data?.facility_id && !currentAmName;

  // ------------------------------------------------------------------------
  // AFB-0082, 2026-08-19 — this screen now knows the off-project vocabulary
  // exists.
  //
  // The classification is NOT editable here: it is set at entry and changed from
  // the classification queue. It is read for two reasons. It decides whether
  // `reason_note` is required (the DB CHECK exempts only 'Project'), and a screen
  // that cannot say "this is a Recovery" is describing a record it is not
  // showing — which is how Jack came to be looking at what appeared to be an
  // ordinary project failing to save on a column the page never mentioned.
  //
  // `event_class` is already in PROJECT_SELECT and already typed on ProjectRow.
  // No extra query and no type change.
  // ------------------------------------------------------------------------
  const isOffProject = (data?.event_class ?? PROJECT_EVENT_CLASS) !== PROJECT_EVENT_CLASS;
  const {
    eventClasses,
    reasons,
    isLoading: catalogLoading,
    error: catalogError,
  } = useServiceCatalog();
  // The AM-facing WORDS come from the database — v_event_classes for the
  // division, special_reasons for the reason. Never literals in src/ (PLAYBOOK;
  // scripts/check-vocab-literals.mjs).
  const eventClassOption = useMemo(
    () => eventClasses.find((c) => c.event_class === data?.event_class) ?? null,
    [eventClasses, data?.event_class],
  );
  const reasonOption = useMemo(
    () => reasons.find((r) => r.reason_code === data?.reason_code) ?? null,
    [reasons, data?.reason_code],
  );
  // The note the database insists on, missing. Blocks Save in words rather than
  // letting the constraint speak for itself.
  const noteRequiredMissing = isOffProject && !edit?.reason_note.trim();

  // ------------------------------------------------------------------------
  // Run C Phase 2 — the lines are the authority for a project's money.
  //
  // Once a project has service lines, its bill / pay / sub / dates are DERIVED
  // from them and read-only here, using the same functions the entry screen uses
  // (`lib/entry-derive.ts`) so the two screens cannot compute a project two ways.
  // A project with NO lines — eight of them exist — keeps the old editable
  // fields, because there is nothing to derive from.
  // ------------------------------------------------------------------------
  // One copy of this derivation, shared with Ask AI's project card (lib/ready-gate.ts).
  const gate = useMemo(() => readyGate(data, edit, lines), [data, edit, lines]);
  const { saveableLines, hasLines, derivedAmounts, derivedDates } = gate;
  const isMultiLine = saveableLines.length >= 2;
  const lineDiff = useMemo(() => diffLines(originalLines, lines), [originalLines, lines]);

  // 2026-08-21: lineSubIds is gone with the read-only project-level sub slot it
  // fed. The lines themselves name their subs, one per line, right above.

  const isDirty = useMemo(() => {
    if (!data || !edit) return false;
    const base = rowToEditState(data);
    const fieldsDirty = (Object.keys(base) as Array<keyof EditState>).some((k) => base[k] !== edit[k]);
    return fieldsDirty || lineDiff.dirty;
  }, [data, edit, lineDiff.dirty]);

  const { effectiveCompletion, billAmtNum, payAmtNum, readyCompletion } = gate;
  // Ready-to-Invoice gate (2026-06-03): a completion date is now required in
  // addition to bill + pay before a Draft can move to finance.
  //
  // AFB-0082, 2026-08-19 — a STATED zero is a decision; a NULL is an omission.
  // Five of the six event classes normally bill the client nothing (Recovery,
  // Concession, Coverage, Startup, and Correction when it credits), so `> 0` made
  // every non-billable record permanently unadvanceable — and since Run C Phase 2
  // made bill read-only wherever service lines exist, there was no input on the
  // screen that could change it. Ten rows carry bill_amount = 0 today; not one of
  // them could ever have been moved.
  //
  // billAmtNum / payAmtNum are already NULL unless EVERY line carries a rate
  // (deriveAmounts), so "stated" is the honest completeness test and the
  // TCSD-0061 / TCSD-0063 guards still do their own job downstream.
  //
  // 2026-09-02 — the gate and its words BOTH come from lib/advance-gate.ts now.
  // This page used to spell out the condition and then build its own list of
  // blocker strings beside it; the board card did the same, and the two had
  // already drifted in wording and order under a comment promising they had not.
  // The lines go in so the sentence can say WHICH one is short instead of
  // reporting a NULL project total the AM can see is not the whole story.
  // ⛔ Do not restate either here. advance-gate.lockstep.test.ts will fail.
  const gateFields = gate.gateFields;
  const canReadyToInvoice = canAdvance(gateFields);
  const readyBlockers = getAdvanceBlockers(gateFields);

  // 2026-09-02 — attribution, DERIVED, NEVER ASKED. Same batched hook the board
  // uses; here the list is one id. Both clauses are shown on this screen — this
  // is where you come to ask someone about the record, so you need to know who
  // to ask AND whose number it moves.
  // ⛔ No form field for either. The person is the signed-in user; the owner is
  // the account manager on the building.
  const attributionIds = useMemo(() => (projectId ? [projectId] : []), [projectId]);
  const attribution = useProjectAttribution(attributionIds);
  const attributionRow = projectId ? (attribution.rows.get(projectId) ?? null) : null;
  const attributionEntered = attributionRow
    ? enteredClause(attributionRow, attribution.nameFor)
    : null;
  const attributionOwner = attributionRow
    ? ownerClause(attributionRow, attribution.nameFor)
    : null;
  const attributionCaveat = attributionRow ? ownerCaveat(attributionRow) : null;

  // ⛔ Is MONEY what is stopping this? The blocker list already separates the
  // faults one by one, so this is a filter on an answer that exists — not a
  // second copy of the gate (advance-gate.lockstep.test.ts would fail on one).
  const moneyIsBlocking = readyBlockers.some(
    (b) => b.includes('bill') || b.includes('pay') || b.includes('client rate'),
  );
  // ⛔ ONE HELPER, TWO INTENTS. `entry` is an unsent Draft finishing its own
  // classification; `reclassify` is 2A — changing what a record IS after
  // Finance has read it, which owes a written reason the first one does not.
  const [helperOpen, setHelperOpen] = useState<null | 'entry' | 'reclassify'>(null);
  const [helperBusy, setHelperBusy] = useState(false);

  // A WARNING, never a block. An AM zeroing a real project by accident is the
  // risk `> 0` was covering for, and that cover is worth keeping — but it costs
  // a sentence, not a dead end.
  const zeroBillOnOrdinaryWork = billAmtNum === 0 && !isOffProject;

  // Door 2 hands the helper what the record already knows, so the building, the
  // date and the sub are shown rather than asked a second time.
  const storedLineSubs = Array.from(
    new Set(
      (data?.project_services ?? [])
        .filter((l) => l.line_role !== 'chargeback')
        .map((l) => l.assigned_partner_id)
        .filter((v): v is string => !!v),
    ),
  );
  const helperPrefill = [
    { label: 'Building', value: data?.facilities?.facility_name ?? data?.facility_name ?? '—' },
    { label: 'Date', value: readyCompletion ?? 'not set' },
    // Item #680: on a record with lines, each line names its own sub; only a
    // billing-comms note with no line names one on the record (Decision 2).
    { label: 'Sub', value: storedLineSubs.length ? storedLineSubs.join(', ') : data?.assigned_partner_id ?? 'not set' },
  ].filter((f) => f.value !== '—');

  // ⛔ DOOR 2 — what the helper's answer DOES to a stuck card.
  //
  // The helper hands back a `division_id`, never an `event_class`: the division
  // is what the AM effectively picked, and the class it belongs to is read from
  // the catalog, exactly as the entry screen does it (am-entry-screen.tsx:143).
  // Resolving it here from a literal would be a second copy of the vocabulary
  // and check-vocab-literals.mjs is right to forbid that.
  //
  // ⭐ 2A IS THE SAME FUNCTION WITH A WIDER DOOR — and the reason it can be is a
  // measured one: RECLASSIFYING MOVES NO MONEY. Exactly three things in the
  // database read `event_class` (a mapping function, the catalog, and Jose's
  // queue); no invoice function, no margin trigger and no payment path touch it.
  // I checked before building rather than after. What the sent case owes is a
  // WRITTEN REASON, not a permission — TCSD-0170 already settled who may edit,
  // and `audit_log` records every actor but cannot record a why.
  async function handleHelperDone(outcome: HelperOutcome) {
    if (!data || !employee?.employee_id) return;
    const intent = helperOpen;
    setHelperOpen(null);
    setHelperBusy(true);
    setSaveError(null);
    try {
      const resolved = outcome.division_id
        ? (eventClasses.find((c) => c.division_id === outcome.division_id)?.event_class ?? null)
        : null;
      // The fall-through — and ONLY the fall-through — leaves it for Finance.
      const nextClass = outcome.unclassified ? UNCLASSIFIED_EVENT_CLASS : resolved;
      if (!nextClass) {
        setSaveError(
          "Couldn't work out what to file this as. Pick a kind on the form, or say what happened in the note.",
        );
        return;
      }

      // ---- 2A · re-filing a record Finance has already read ----------------
      if (intent === 'reclassify') {
        const move = {
          event_class: nextClass,
          reason_code: outcome.unclassified ? null : outcome.reason_code,
          words: outcome.words,
          sent: !isNotSent(stage),
          current_event_class: data.event_class ?? null,
        };
        const missing = reclassifyBlockers(move);
        if (missing.length > 0) {
          setSaveError(`Can't re-file this yet — it still needs ${missing.join(' and ')}.`);
          return;
        }
        // ⛔ The old note is KEPT and the new reason stacked on top. What the
        // record used to say about itself is the only account of why it was
        // filed that way, and this is the moment somebody wants to read it.
        await updateProject(
          data.project_id,
          reclassifyPatch(move, data.reason_note ?? null, new Date().toISOString().slice(0, 10)),
          employee.employee_id,
        );
        refetch();
        return;
      }

      // ---- finishing entry on an unsent Draft ------------------------------
      const patch: UpdateProjectInput = {
        event_class: nextClass,
        reason_code: outcome.unclassified ? null : outcome.reason_code,
      };
      // The words the AM typed are the whole payload on the fall-through, so
      // they are never dropped; on a classified answer they are extra context
      // and must not erase a note already on the record.
      if (outcome.words.trim()) patch.reason_note = outcome.words.trim();
      // ⭐ THE HELPER'S OTHER ANSWERS. Second pass, 2026-09-03 — the first one
      // took the classification and threw away where, when and who, which are
      // the three facts that let Finance finish the job.
      //
      // ⛔ FILL, NEVER OVERWRITE. This record already exists; an answer given in
      // a hurry inside a modal must not replace a completion date or a sub that
      // is already on it. The helper adds what is missing and nothing else.
      if (outcome.date_from && !data.completion_date) patch.completion_date = outcome.date_from;
      // Item #680 (call 3): only a record with NO line takes the sub on itself —
      // a billing-comms note (Decision 2). With lines, each line names its own
      // sub and the record's slot is not written.
      if (outcome.assigned_partner_id && !data.assigned_partner_id && (data.project_services ?? []).length === 0) {
        patch.assigned_partner_id = outcome.assigned_partner_id;
      }
      await updateProject(data.project_id, patch, employee.employee_id);
      refetch();
    } catch (err) {
      setSaveError(errorMessage(err, 'Failed to save what the helper worked out.'));
    } finally {
      setHelperBusy(false);
    }
  }

  function field<K extends keyof EditState>(k: K, v: EditState[K]) {
    setEdit((prev) => (prev ? { ...prev, [k]: v } : prev));
  }

  function patchLine(key: string, patch: Partial<EntryLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  async function handlePayLine(serviceLineId: string) {
    setPayBusy(serviceLineId);
    setPayError((prev) => ({ ...prev, [serviceLineId]: '' }));
    try {
      await mintProjectServiceLinePayment(serviceLineId, true);
      setPayVersion((v) => v + 1);
    } catch (err) {
      setPayError((prev) => ({
        ...prev,
        [serviceLineId]: rpcErrorMessage(err, 'Could not pay that service line.'),
      }));
    } finally {
      setPayBusy(null);
    }
  }

  async function handleRemoveLine() {
    if (!removeLine) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      const remaining = lines
        .filter((l) => l.key !== removeLine.key)
        .filter((l) => lineIsSaveable(l, OTHER_SERVICE_CODE));

      if (removeLine.service_line_id) {
        await deleteProjectServiceLine(removeLine.service_line_id, removeReason);

        // Reconcile the project's derived money IN THE SAME ACTION.
        //
        // Removing a line changes the totals, and nothing else in the form is
        // dirty afterwards — so without this the Save button never appears and
        // the project is left storing a total its own lines no longer add up to,
        // with the screen saying so and offering no way to fix it. The removal
        // is the event that changed the money, so the removal reconciles it,
        // which is also exactly what the confirm dialog promises.
        //
        // Same NULL rule as everywhere else: a derived NULL is never written
        // over a stored value.
        if (data && employee?.employee_id) {
          const amounts = deriveAmounts(remaining);
          const dates = deriveDates(remaining);
          const patch: UpdateProjectInput = {};
          if (amounts.bill_amount !== null) patch.bill_amount = amounts.bill_amount;
          if (amounts.pay_amount !== null) patch.pay_amount = amounts.pay_amount;
          if (dates.start_date !== null) patch.start_date = dates.start_date;
          if (dates.completion_date !== null) patch.completion_date = dates.completion_date;
          if (Object.keys(patch).length > 0) {
            await updateProject(data.project_id, patch, employee.employee_id);
          }
        }
      }
      // An unsaved line has nothing to snapshot — drop it from the list only.
      setLines((prev) => prev.filter((l) => l.key !== removeLine.key));
      setOriginalLines((prev) => prev.filter((l) => l.key !== removeLine.key));
      setRemoveLine(null);
      setRemoveReason('');
      refetch();
    } catch (err) {
      setRemoveError(rpcErrorMessage(err, 'Could not remove that service line.'));
    } finally {
      setRemoveBusy(false);
    }
  }

  async function handleSave() {
    if (!data || !edit || !employee?.employee_id) return;
    // AFB-0082 — refuse in words, here, rather than letting the user meet
    // `projects_reason_note_required_off_project` as a raw 23514.
    if (noteRequiredMissing) {
      setSaveError(OFF_PROJECT_NOTE_REQUIRED);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // ------------------------------------------------------------------
      // Run C Phase 2 — LINES FIRST, then the project row derived from them.
      //
      // Order matters. The project's money is computed from the lines, so the
      // lines have to be the ones that are true when it is written; and a line
      // write that is refused must not leave the project already carrying a
      // total the lines never agreed to.
      // ------------------------------------------------------------------
      const currentSaveable = lines.filter((l) => lineIsSaveable(l, OTHER_SERVICE_CODE));
      for (const l of lines) {
        if (!lineIsSaveable(l, OTHER_SERVICE_CODE)) continue;
        const order = currentSaveable.findIndex((x) => x.key === l.key) + 1;
        const isNew = !l.service_line_id;
        const wasChanged = lineDiff.changed.some((c) => c.key === l.key);
        const wasMoved = lineDiff.reordered.some((c) => c.key === l.key);
        if (!isNew && !wasChanged && !wasMoved) continue;
        const id = await saveProjectServiceLine(
          data.project_id,
          entryLineToInput(l, order),
          l.service_line_id || null,
        );
        if (isNew && id) l.service_line_id = id;
      }

      const patch: UpdateProjectInput = {
        notes: edit.notes.trim() || null,
        due_date: edit.due_date || null,
        // A completed job is no longer waiting on the client for a date.
        awaiting_schedule: effectiveCompletion ? false : edit.awaiting_schedule,
        // A note is required by projects_reason_note_required_off_project on EVERY
        // row whose event_class is not 'Project'. AFB-0022 #3 only ever meant to
        // clear the note that belongs to the special_pay checkbox; Run A made the
        // same column carry the off-project classification note, and clearing that
        // one is a 23514 on save (AFB-0082, 2026-08-19).
        special_pay: edit.special_pay,
        reason_note: isOffProject
          ? edit.reason_note.trim() || null
          : edit.special_pay && edit.reason_note.trim()
            ? edit.reason_note.trim()
            : null,
      };

      if (hasLines) {
        // Derived. A NULL derivation means "not every line carries this yet" and
        // is NEVER written over a stored value — that would drop an amount off an
        // invoice, or clear the completion date a mint is gated on.
        if (derivedAmounts.bill_amount !== null) patch.bill_amount = derivedAmounts.bill_amount;
        if (derivedAmounts.pay_amount !== null) patch.pay_amount = derivedAmounts.pay_amount;
        if (derivedDates.start_date !== null) patch.start_date = derivedDates.start_date;
        if (derivedDates.completion_date !== null) patch.completion_date = derivedDates.completion_date;
        // ⛔ The project's subcontractor is NOT written when the record has lines.
        // Each line names and pays its own sub (TCSD-0380, item #680, call 3), so
        // copying a line's sub up here is a second copy of one fact. The stored
        // value is left exactly as it is — never cleared — and nothing that pays
        // reads it.
      } else {
        patch.bill_amount = parseAmount(edit.bill_amount);
        patch.pay_amount = parseAmount(edit.pay_amount);
        patch.start_date = edit.start_date || null;
        patch.completion_date = edit.completion_date || null;
        // A billing-comms note with no line names its sub here, on the record
        // (Decision 2, 2026-10-02: the note stays as built). Jose pays it from the
        // AP report; the app creates no payable from it.
        patch.assigned_partner_id = edit.assigned_partner_id || null;
      }

      await updateProject(data.project_id, patch, employee.employee_id);
      refetch();
    } catch (err) {
      setSaveError(errorMessage(err, 'Failed to save changes.'));
    } finally {
      setSaving(false);
    }
  }

  // 2026-07-20 — link/relink (or clear) the project's facility. Selecting a
  // facility auto-derives the account (id + name) from the facility's account;
  // updateProject() stamps updated_by_eid and the DB bumps updated_at.
  async function handleFacilityLink(facility: Facility | null) {
    if (!data || !employee?.employee_id) return;
    setFacilityBusy(true);
    setFacilityError(null);
    try {
      let patch: UpdateProjectInput;
      if (!facility) {
        patch = { facility_id: null, facility_name: null, account_id: null, account_name: null };
      } else {
        // Derive the account from the chosen facility (account_name isn't on the
        // facility row). facility.account_id comes back with the picker's row.
        let accountName: string | null = null;
        const accountId = facility.account_id ?? null;
        if (accountId) {
          const { data: acct, error: acctErr } = await supabase
            .from('accounts')
            .select('account_name')
            .eq('account_id', accountId)
            .maybeSingle();
          if (acctErr) throw acctErr;
          accountName = (acct as { account_name: string | null } | null)?.account_name ?? null;
        }
        patch = {
          facility_id: facility.facility_id,
          facility_name: facility.facility_name ?? null,
          account_id: accountId,
          account_name: accountName,
        };
      }
      await updateProject(data.project_id, patch, employee.employee_id);
      setFacilityEditOpen(false);
      refetch();
    } catch (err) {
      setFacilityError(errorMessage(err, 'Failed to update the facility.'));
    } finally {
      setFacilityBusy(false);
    }
  }

  async function advanceStage(next: ProjectStage, toast: string) {
    if (!data || !employee?.employee_id) return;
    setStageBusy(next);
    setStageError(null);
    try {
      await updateProjectStage(data.project_id, next, employee.employee_id);
      setStageToast(toast);
      refetch();
    } catch (err) {
      setStageError(errorMessage(err, 'Failed to advance stage.'));
    } finally {
      setStageBusy(null);
    }
  }

  // AFB-0009 — finance/exec bounces the card back to the AM with a reason.
  async function handleSendBack() {
    if (!data || !employee?.employee_id) return;
    const reason = sendBackReason.trim();
    if (!reason) return;
    setSendBackBusy(true);
    setStageError(null);
    try {
      await sendProjectBackToAm(data.project_id, reason, employee.employee_id);
      setSendBackOpen(false);
      setSendBackReason('');
      setStageToast('Sent back to the AM. They’ll be notified next time they open Project Work.');
      refetch();
    } catch (err) {
      setStageError(errorMessage(err, 'Failed to send the card back to the AM.'));
    } finally {
      setSendBackBusy(false);
    }
  }

  // AFB-0009 — AM marks the missing data addressed; card returns to Ready to Invoice.
  async function handleResubmit() {
    if (!data || !employee?.employee_id) return;
    setResubmitBusy(true);
    setStageError(null);
    try {
      await resolveAwaitingAmInput(data.project_id, employee.employee_id);
      setStageToast('Marked addressed — back to Ready to Invoice for finance.');
      refetch();
    } catch (err) {
      setStageError(errorMessage(err, 'Failed to update the stage.'));
    } finally {
      setResubmitBusy(false);
    }
  }

  // AFB-0022 #1 — delete the project (reason required), then leave the board.
  // ---- 2B · "Repeat this one" ------------------------------------------
  //
  // The commonest thing an AM types is a job they have typed before: the same
  // work, at the same building, with the same sub, at the same rate, a month
  // later. Doing that from an empty form is how a rate gets fat-fingered.
  //
  // ⛔ The RULES are in lib/repeat-project.ts and are not restated here — what
  // travels, what is dropped, and why a chargeback is never copied forward.
  const [repeatBusy, setRepeatBusy] = useState(false);

  async function handleRepeat() {
    if (!data || !employee?.employee_id) return;
    setRepeatBusy(true);
    setSaveError(null);
    try {
      const { project_id } = await repeatProject(data.project_id, employee.employee_id);
      // Land the AM ON the new Draft. A toast saying "created" and leaving them
      // on the old record is how you get two people editing the wrong one.
      navigate(`/project-work/${project_id}`);
    } catch (err) {
      setSaveError(errorMessage(err, 'Could not repeat this project.'));
    } finally {
      setRepeatBusy(false);
    }
  }

  async function handleDelete() {
    if (!data) return;
    const reason = deleteReason.trim();
    if (!reason) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteProject(data.project_id, reason);
      navigate('/project-work');
    } catch (err) {
      // rpcErrorMessage, not errorMessage: delete_project's refusals carry the
      // sentence the user needs (TCS_PROJ_PAID_DELETE_NOT_AUTHORIZED names the
      // payment and says who can), and a generic fallback would swallow it.
      setDeleteError(rpcErrorMessage(err, 'Failed to delete the project.'));
      setDeleteBusy(false);
    }
  }

  // AFB-0022 #4 — assign the facility's AM, then refresh so the "No AM assigned"
  // warning clears and the AM badge resolves.
  async function handleAssignAm() {
    if (!data?.facility_id || !assignEid) return;
    setAssignBusy(true);
    setAssignError(null);
    try {
      await assignFacilityAm(data.facility_id, assignEid);
      setAssignEid('');
      setAmEditOpen(false);
      refetch();
    } catch (err) {
      setAssignError(
        errorMessage(err, 'Failed to assign the account manager.'),
      );
    } finally {
      setAssignBusy(false);
    }
  }

  function handleCancel() {
    if (data) setEdit(rowToEditState(data));
    setSaveError(null);
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {/* DOOR 2. Mounted at the top of the page, not inside the stage block, so
          the modal is never clipped by the card it was opened from. It stays
          unrendered until `open`; the component resets its own steps on open. */}
      <ClassifyHelperDialog
        open={helperOpen !== null}
        onClose={() => setHelperOpen(null)}
        onDone={(o: HelperOutcome) => { void handleHelperDone(o); }}
        prefilled={helperPrefill}
        initialWords={data?.reason_note ?? ''}
        // What the record already knows, so the helper opens on the AM's own
        // answers rather than a blank form they have to re-type.
        initialFacilityIds={data?.facility_id ? [data.facility_id] : []}
        initialDate={data?.completion_date ?? ''}
        initialPartnerId={data?.assigned_partner_id ?? ''}
      />

      <div className="mb-3 flex items-center justify-between gap-3">
        <Link to="/project-work" className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary hover:text-tcs-text">
          <ArrowLeft className="h-4 w-4" /> All projects
        </Link>
        {/* Run B Phase 5 — a preview of what a client would be quoted. Read
            only; nothing is sent, signed or stored by opening it. */}
        {projectId && (
          <Link
            to={`/project-work/${projectId}/quote`}
            className="text-sm text-tcs-text-secondary hover:text-tcs-text"
          >
            Quote preview
          </Link>
        )}
      </div>

      {error && (
        <ErrorState title="Couldn't load project" body={error} onRetry={refetch} />
      )}

      {isLoading && !error && (
        <div className="space-y-3">
          <Skeleton className="h-8 w-1/2 rounded-md" />
          <Skeleton className="h-40 w-full rounded-md" />
        </div>
      )}

      {!isLoading && !error && !data && (
        <div className="rounded-md border border-tcs-border bg-tcs-bg p-6 text-center text-sm text-tcs-text-secondary">
          Project not found, or you don't have permission to view it.
        </div>
      )}

      {!isLoading && !error && data && edit && (
        <>
          <PageHeader
            eyebrow={
              <span className="inline-flex items-center gap-2">
                <span className="font-mono">{data.project_id}</span>
                {/* AFB-0030 — linkable AP reference for this project-work
                    payment, so a line on the AP worksheet traces back here. */}
                <CopyReference
                  id={data.project_id}
                  path={`/project-work/${data.project_id}`}
                  detail={
                    data.pay_amount != null
                      ? `sub payment $${data.pay_amount}`
                      : undefined
                  }
                />
              </span>
            }
            title={data.facilities?.facility_name ?? data.facility_name ?? '—'}
            meta={
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-tcs-text-secondary">
                <span>{data.accounts?.account_name ?? data.account_name ?? '—'}</span>
                {/* AFB-0006 — Account Manager (source of truth: facility EID join) */}
                {(() => {
                  // 2026-09-02 — WHOSE IT IS, on the date the work was done.
                  // `ownerClause` walks audit_log back through
                  // v_project_attribution; the facility's CURRENT manager is only
                  // the fallback while the batch is in flight. 109 of 477
                  // projects sit on a building that has changed hands since.
                  const am = data.facilities?.account_manager;
                  const amName = attributionOwner
                    ? attributionOwner
                    : am
                      ? `${am.employee_name_first ?? ''} ${am.employee_name_last ?? ''}`.trim()
                      : null;
                  if (!data.facility_id) {
                    return (
                      <span
                        className="inline-flex items-center gap-1 rounded-md border border-rag-red/40 bg-rag-red/10 px-1.5 py-[2px] text-[11px] font-medium text-rag-red"
                        title="This project has no facility linked."
                      >
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                        No facility linked
                      </span>
                    );
                  }
                  if (!amName) {
                    return (
                      <span
                        className="inline-flex items-center gap-1 rounded-md border border-rag-amber/40 bg-st-warn-bg px-1.5 py-[2px] text-[11px] font-medium text-st-warn-fg"
                        title="No account manager is assigned to this facility. Update the facility record to clear this warning."
                      >
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                        No AM assigned
                      </span>
                    );
                  }
                  return (
                    <span
                      className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-1.5 py-[2px] text-[11px] font-medium text-tcs-text-secondary"
                      // ⚠️ Present only when the name is the earliest value on
                      // file rather than a recorded fact — the account-manager
                      // trail begins 2026-04-29 and 64% of projects predate it.
                      title={attributionCaveat ?? undefined}
                    >
                      <User className="h-3 w-3" aria-hidden="true" />
                      {amName}
                    </span>
                  );
                })()}
                {/* AFB-0010 — WHO TYPED IT, beside whose account it is.
                    2026-09-02: this read data.creator directly and printed
                    "Submitted by <name>" on all 345 script-loaded rows — a living
                    employee's name on a record a load wrote (⛔ TCSD-0150). It now
                    comes from the attribution row, which returns NO PERSON for a
                    script row and names the load instead. */}
                {attributionEntered && (
                  <span
                    className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-1.5 py-[2px] text-[11px] font-medium text-tcs-text-secondary"
                    title="Who typed this record. A record loaded by an import names the load, never a person."
                  >
                    <UserPlus className="h-3 w-3" aria-hidden="true" />
                    {attributionEntered}
                  </span>
                )}
              </span>
            }
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {stage && (
                  <span className={`inline-flex items-center rounded-md px-2 py-[2px] text-[11px] font-semibold uppercase tracking-wide ${PROJECT_STAGE_CLASSES[stage] ?? 'bg-tcs-surface text-tcs-text-secondary'}`}>
                    {PROJECT_STAGE_LABELS[stage] ?? stage}
                  </span>
                )}
              </div>
            }
          />

          {/* AFB-0082 — how this record is classified, read-only.
              Set when it was entered; changed from the classification queue, not
              here. Before this existed the page rendered a Recovery as though it
              were an ordinary project, and refused to save on a column it never
              mentioned. The WORDS are read from the database (v_event_classes,
              special_reasons) — never written here. */}
          {isOffProject && (
            <div className="mt-4 rounded-md border border-rag-amber/40 bg-st-warn-bg p-3">
              <span className="block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                Not ordinary billable work
              </span>
              <div className="mt-0.5 text-sm text-tcs-text">
                {eventClassOption ? (
                  <>
                    <span className="font-semibold">{eventClassOption.display_name}</span>
                    {reasonOption && (
                      <span className="text-tcs-text-secondary"> · {reasonOption.display_name}</span>
                    )}
                    {eventClassOption.description && (
                      <span className="text-tcs-text-muted"> — {eventClassOption.description}</span>
                    )}
                  </>
                ) : catalogLoading ? (
                  <span className="text-tcs-text-muted">Reading how this record is classified…</span>
                ) : (
                  /* Degrade to what is still true rather than naming a division
                     that may have been retired, or echoing the structural value
                     as though it were a label. */
                  <span className="text-tcs-text-muted">
                    Classified as something other than ordinary project work. The current list
                    does not have a name for it, so this screen will not invent one.
                    {catalogError ? ` (${catalogError})` : ''}
                  </span>
                )}
              </div>
              <p className="mt-1 text-[11px] text-tcs-text-muted">
                Set when this was entered. Changed from the classification queue, not here.
              </p>
            </div>
          )}

          {/* 2026-07-20 — link/relink the project to a facility. Selecting one
              auto-derives the account; the convert-from-billing-comm path now
              carries a real facility, but historical/legacy projects (facility
              only in notes) can be fixed here. Searches Closed facilities too. */}
          <div className="mt-4 rounded-md border border-tcs-border bg-tcs-bg p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <span className="block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                  Facility
                </span>
                <div className="text-sm text-tcs-text">
                  {data.facility_id ? (
                    <>
                      <span className="font-semibold">
                        {data.facilities?.facility_name ?? data.facility_name ?? data.facility_id}
                      </span>
                      <span className="text-tcs-text-muted"> · {data.facility_id}</span>
                      {(data.accounts?.account_name ?? data.account_name) && (
                        <span className="text-tcs-text-secondary">
                          {' '}— {data.accounts?.account_name ?? data.account_name}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rag-red">
                      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> No facility linked
                    </span>
                  )}
                </div>
              </div>
              {!facilityEditOpen && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFacilityError(null);
                    setFacilityEditOpen(true);
                  }}
                >
                  {data.facility_id ? 'Change' : 'Link facility'}
                </Button>
              )}
            </div>

            {facilityEditOpen && (
              <div className="mt-3 space-y-2">
                <FacilityPicker
                  value={null}
                  includeClosed
                  placeholder="Search facilities (incl. closed)…"
                  onChange={(f) => {
                    if (f) void handleFacilityLink(f);
                  }}
                />
                <div className="flex flex-wrap items-center gap-2">
                  {data.facility_id && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={facilityBusy}
                      onClick={() => void handleFacilityLink(null)}
                    >
                      Remove facility
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={facilityBusy}
                    onClick={() => setFacilityEditOpen(false)}
                  >
                    Cancel
                  </Button>
                  {facilityBusy && (
                    <Loader2 className="h-4 w-4 animate-spin text-tcs-text-muted" aria-hidden="true" />
                  )}
                </div>
                {facilityError && <p className="text-xs text-rag-red">{facilityError}</p>}
              </div>
            )}
          </div>

          {/* AFB-0022 #4 / 2026-06-04 — add or edit the facility's AM. Anyone
              setting up a not-yet-final facility can add/edit it; once the
              facility is Active (final) changing it requires full AM authority. */}
          {!!data.facility_id && (
            <div
              className={`mt-4 rounded-md border p-3 ${
                facilityMissingAm
                  ? 'border-rag-amber/40 bg-rag-amber/10'
                  : 'border-tcs-border bg-tcs-bg'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                {facilityMissingAm ? (
                  <span className="flex items-center gap-1.5 text-sm font-medium text-st-warn-fg">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                    No account manager assigned to this facility
                  </span>
                ) : (
                  <span className="text-sm text-tcs-text">
                    Account manager: <span className="font-semibold">{currentAmName}</span>
                  </span>
                )}
                {!facilityMissingAm && canAssignAm && !amEditOpen && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAssignEid('');
                      setAmEditOpen(true);
                    }}
                  >
                    Change
                  </Button>
                )}
              </div>

              {(facilityMissingAm || amEditOpen) &&
                (canAssignAm ? (
                  <>
                    <p className="mt-1 text-xs text-tcs-text-secondary">
                      {facilityIsFinal
                        ? 'This facility is finalized — your change is logged on the facility record.'
                        : 'Anyone setting up this facility can add or edit the AM until it is finalized.'}
                    </p>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <select
                        value={assignEid}
                        onChange={(e) => setAssignEid(e.target.value)}
                        className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text sm:max-w-xs"
                      >
                        <option value="">Select an account manager…</option>
                        {amEligible.map((o) => (
                          <option key={o.employee_id} value={o.employee_id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={handleAssignAm}
                        disabled={!assignEid || assignBusy}
                      >
                        {assignBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                        <UserPlus className="h-4 w-4" />
                        {facilityMissingAm ? 'Assign AM' : 'Save AM'}
                      </Button>
                      {!facilityMissingAm && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setAmEditOpen(false)}
                          disabled={assignBusy}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="mt-1 text-xs text-tcs-text-secondary">
                    This facility is finalized — ask a national AM or admin to change the account manager.
                  </p>
                ))}
              {assignError && <p className="mt-2 text-xs text-rag-red">{assignError}</p>}
            </div>
          )}

          {/* Task 1 — route this record to a person + see its routing history. */}
          <div className="mt-4 rounded-md border border-tcs-border bg-tcs-surface/40 p-4">
            <h3 className="text-sm font-semibold text-tcs-text">Routing &amp; activity</h3>
            <div className="mt-3">
              <RouteToPanel
                entityType="project"
                entityId={data.project_id}
                onRouted={() => setRoutingVersion((v) => v + 1)}
              />
            </div>
            <div className="mt-3">
              <RecordActivityLog
                key={routingVersion}
                entityType="project"
                entityId={data.project_id}
              />
            </div>
          </div>

          {/* Option B (2026-08-21) — the work on the left, the numbers on the
              right. Every fact appears once. Before this the card rendered 28
              fields, 11 of them repeats: stage three times, bill and pay three
              times each, the sub and both dates twice. */}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:items-start">
          <div className="min-w-0">
          {/* ---- Run C Phase 2 — every service line, editable in place ---- */}
          {/* id: a Home row lands here when the gate is short on a line (item #650). */}
          <div id="work-focus-lines" className="mt-4 scroll-mt-24 rounded-md border border-tcs-border bg-tcs-bg p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-tcs-text">
                Services{lines.length > 0 ? ` · ${lines.length}` : ''}
              </h3>
              {data.service_type_raw && (
                <span className="text-[11px] text-tcs-text-muted">
                  Originally recorded as “{data.service_type_raw}”
                </span>
              )}
            </div>

            {lines.length === 0 && (
              <p className="mt-2 text-[12px] text-tcs-text-secondary">
                No service lines on this record. Its bill, pay and subcontractor are still edited
                directly below. Adding a line makes the lines the authority instead.
              </p>
            )}

            <div className="mt-3 space-y-3">
              {lines.map((l, i) => (
                <div key={l.key}>
                  <ServiceLineCard
                    line={l}
                    index={i}
                    total={lines.length}
                    facilityId={data.facility_id ?? null}
                    projectId={projectId ?? null}
                    otherServiceCode={OTHER_SERVICE_CODE}
                    onChange={(patch) => patchLine(l.key, patch)}
                    onRemove={() => {
                      setRemoveLine(l);
                      setRemoveReason('');
                      setRemoveError(null);
                    }}
                  />
                  {l.service_line_id && (
                    <p className="mt-1 px-1 text-[10.5px] text-tcs-text-muted">
                      {l.service_line_id}
                      {l.service_date_actual
                        ? ' · done'
                        : l.service_date_planned
                          ? ' · scheduled'
                          : ' · estimate'}
                    </p>
                  )}
                  {/* Run C Phase 4 — what paying THIS line would do, or why it
                      cannot be paid. The whole set is visible before any of it
                      is clicked. */}
                  {l.service_line_id && (canFinance || hasFullAuthority) && (
                    <LinePaymentPanel
                      row={linePayments.find((p) => p.service_line_id === l.service_line_id)}
                      dirty={lineDiff.changed.some((c) => c.key === l.key) || !l.service_line_id}
                      busy={payBusy === l.service_line_id}
                      error={payError[l.service_line_id] || null}
                      onPay={() => handlePayLine(l.service_line_id)}
                    />
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() =>
                setLines((prev) => [...prev, blankLine(`new-${Date.now()}-${prev.length}`)])
              }
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-tcs-border px-3 py-2 text-xs text-tcs-text-secondary hover:border-tcs-inverse/40 hover:text-tcs-text"
            >
              + Add another service line
            </button>

            {isMultiLine && (
              <p className="mt-3 flex gap-2 rounded-md bg-st-info-bg px-3 py-2 text-[11.5px] text-st-info-fg">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {saveableLines.length} service lines, so this project's bill, pay and
                  subcontractor are read below from the lines rather than typed. Each line is paid
                  its own subcontractor, one at a time — one project amount cannot be split across
                  several by guessing.
                </span>
              </p>
            )}
            {linePaymentsError && (
              <p className="mt-3 rounded-md bg-st-warn-bg px-3 py-2 text-[11.5px] text-st-warn-fg">
                Payment state could not be read, so this screen is not showing whether these lines
                have been paid. {linePaymentsError}
              </p>
            )}
          </div>

          {/* Editable fields panel */}
          <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 rounded-md border border-tcs-border bg-tcs-bg p-4 sm:grid-cols-2">
            {hasLines ? null : (
              <div className="block">
                <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">Partner / subcontractor</span>
                <SmartSelect
                  entity="service_provider"
                  value={edit.assigned_partner_id || null}
                  onChange={(id) => field('assigned_partner_id', id || '')}
                  placeholder="Search subcontractors…"
                />
              </div>
            )}
            {hasLines ? null : (
              <LabeledInput id="work-focus-completion_date" type="date" label="Completion date" value={edit.completion_date} onChange={(v) => field('completion_date', v)} />
            )}
            {hasLines ? null : (
              <>
                <LabeledInput id="work-focus-bill_amount" type="number" label="Bill to customer (USD)" value={edit.bill_amount} onChange={(v) => field('bill_amount', v)} />
                <LabeledInput id="work-focus-pay_amount" type="number" label="Pay to subcontractor (USD)" value={edit.pay_amount} onChange={(v) => field('pay_amount', v)} />
              </>
            )}
            {/* ⛔ THE SPECIAL-PAY CHECKBOX IS GONE — Ben, 2026-09-03. There is no
                automatic payment for it to hold: mint_project_sub_payment is an
                EXPLICIT call, never a trigger, so the control's own helper text
                ("Holds the automatic payment") described behaviour that does not
                exist. Measured the same day, the four rows carrying the flag meant
                four different things in their notes — "invoiced within the monthly
                fee", "not billing customer", "Paid Hourly", and one blank — with
                the real answer in free text no report can read.
                ⛔ `projects.special_pay` STAYS as a column: it is a MINT GATE
                (TCSD-0069) and dropping it would change what the payment RPCs
                refuse. Nothing writes it from the UI any more.
                The classification note below is NOT the special-pay note and is
                unchanged — on an off-project record the database requires it. */}
            <div className="sm:col-span-2">
              {isOffProject ? (
                <label className="block">
                  <span className="mb-1 block text-[11px] text-tcs-text-secondary">
                    Say what happened, in words <span className="text-st-danger-fg">*</span>
                  </span>
                  <textarea
                    value={edit.reason_note}
                    onChange={(e) => field('reason_note', e.target.value)}
                    rows={2}
                    required
                    aria-invalid={noteRequiredMissing || undefined}
                    className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                  />
                  {noteRequiredMissing ? (
                    <span className="mt-1 block text-[11px] text-st-warn-fg">
                      {OFF_PROJECT_NOTE_REQUIRED}
                    </span>
                  ) : (
                    <span className="mt-1 block text-[11px] text-tcs-text-muted">
                      This goes to Finance. A code on its own does not explain anything.
                    </span>
                  )}
                </label>
              ) : null}
            </div>
            {/* AFB-0007 — margin is auto-computed by trg_projects_compute_margin.
                Profit ($) shown plain; Margin (%) tinted by margin_status. */}
            <ReadOnlyField
              label="Margin"
              value={
                data.margin_status === 'Internal Cost' ? (
                  <span className="text-tcs-text-muted">Internal Cost</span>
                ) : data.margin_amount != null && data.margin_pct != null ? (
                  <span>
                    {fmtCurrency(data.margin_amount)}{' '}
                    <span className={`font-semibold ${marginColorClass(data.margin_status)}`}>
                      ({fmtPct(data.margin_pct)})
                    </span>
                  </span>
                ) : (
                  <span className="text-tcs-text-muted" title="Add bill and pay amounts to see margin">—</span>
                )
              }
            />
            <ReadOnlyField
              label="Margin status"
              value={
                data.margin_status ? (
                  <span className={`font-semibold ${marginColorClass(data.margin_status)}`}>
                    {data.margin_status}
                  </span>
                ) : '—'
              }
            />
          </div>

          <div className="mt-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">Notes</span>
              <textarea
                value={edit.notes}
                onChange={(e) => field('notes', e.target.value)}
                rows={4}
                className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
              />
            </label>
          </div>

          {saveError && (
            <div className="mt-3">
              <ErrorState title="Couldn't save changes" body={saveError} />
            </div>
          )}

          {isDirty && (
            <div className="mt-4 flex items-center gap-2">
              {/* AFB-0082 — blocked, with the reason already printed under the
                  note field above, so this is never an unexplained dead button. */}
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={saving || noteRequiredMissing}
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <button type="button" onClick={handleCancel} className="text-sm text-tcs-text-secondary hover:text-tcs-text">
                Cancel
              </button>
            </div>
          )}

          {/* Stage / billing pipeline section */}
          <div className="mt-6 rounded-md border border-tcs-border bg-tcs-bg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                  Billing Stage
                </span>
                {stage && (
                  <span className={`inline-flex items-center rounded-md px-2 py-[2px] text-[11px] font-semibold uppercase tracking-wide ${PROJECT_STAGE_CLASSES[stage] ?? 'bg-tcs-surface text-tcs-text-secondary'}`}>
                    {PROJECT_STAGE_LABELS[stage] ?? stage}
                  </span>
                )}
              </div>
            </div>


            {stage === 'Draft' && (
              <div className="mt-3 space-y-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => advanceStage('Ready_to_Invoice', 'Project moved to Ready to Invoice. Finance has been notified.')}
                  disabled={stageBusy != null || !canReadyToInvoice || isDirty}
                >
                  {stageBusy === 'Ready_to_Invoice' && <Loader2 className="h-4 w-4 animate-spin" />}
                  Mark Ready to Invoice
                </Button>
                {/* AFB-0082 — name what is missing, not the whole checklist. */}
                {!canReadyToInvoice && (
                  <div className="text-xs text-st-warn-fg">
                    ⚠ Before this can go to finance: {readyBlockers.join(' · ')}.
                    {/* ⛔ DOOR 2 — AFB-0085. Only when MONEY is what blocks. If the
                        record is short a completion date this button would be an
                        invitation to escape a question the AM can answer, so the
                        condition is a filter on the blocker list rather than a
                        second rule. Ben, 2026-09-03: the button goes to the
                        HELPER, never straight to a text box. */}
                    {moneyIsBlocking && (
                      <div className="mt-1.5">
                        <span className="block italic text-tcs-text-secondary">
                          Can&rsquo;t answer that? It may not be project work.
                        </span>
                        <button
                          type="button"
                          onClick={() => setHelperOpen('entry')}
                          disabled={helperBusy}
                          className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-tcs-border-input bg-tcs-field px-2.5 py-1.5 text-[11.5px] font-semibold text-tcs-text hover:bg-tcs-surface disabled:opacity-50"
                        >
                          {helperBusy && <Loader2 className="h-3 w-3 animate-spin" />}
                          Walk me through it
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {/* A warning, deliberately not a block. */}
                {canReadyToInvoice && zeroBillOnOrdinaryWork && (
                  <div className="text-xs text-st-warn-fg">
                    This is ordinary project work with nothing billed to the client. Check
                    that's right.
                  </div>
                )}
                {isDirty && (
                  <div className="text-xs text-st-warn-fg">
                    Save your pending edits before advancing the stage.
                  </div>
                )}
              </div>
            )}

            {stage === 'Ready_to_Invoice' && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {canFinance ? (
                  <>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => advanceStage('Pending_PO', 'Sent to Pending PO — client billing notice sent, awaiting their PO number.')}
                      disabled={stageBusy != null || sendBackBusy}
                    >
                      {stageBusy === 'Pending_PO' && <Loader2 className="h-4 w-4 animate-spin" />}
                      Send to Pending PO
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => advanceStage('Invoiced', 'Project marked as Invoiced.')}
                      disabled={stageBusy != null || sendBackBusy}
                    >
                      {stageBusy === 'Invoiced' && <Loader2 className="h-4 w-4 animate-spin" />}
                      Mark Invoiced
                    </Button>
                  </>
                ) : (
                  <div className="text-xs text-tcs-text-secondary">
                    Waiting on finance to mark this Invoiced.
                  </div>
                )}
                {/* AFB-0009 — bounce back to the AM for missing data. */}
                {canSendBack && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => { setSendBackReason(''); setStageError(null); setSendBackOpen(true); }}
                    disabled={stageBusy != null || sendBackBusy}
                  >
                    <CornerUpLeft className="h-4 w-4" />
                    Send back to AM
                  </Button>
                )}
              </div>
            )}

            {/* Pending PO — finance sent the client the billing notice and is
                waiting on their PO number before invoicing. Out of our hands;
                follow-ups are logged in the project activity log. */}
            {stage === 'Pending_PO' && (
              <div className="mt-3 space-y-3">
                <div className="rounded-md border border-violet-300 bg-violet-50 px-3 py-2 text-xs text-violet-900">
                  <div className="font-semibold uppercase tracking-wide">Pending client PO</div>
                  <p className="mt-1">
                    Billing notice sent to the client. Waiting on their PO number
                    before we can issue the invoice — this step is out of our
                    hands. Log each follow-up in the activity log below.
                  </p>
                  {data.pending_po_at && (
                    <p className="mt-1 text-[11px] text-violet-700">
                      Waiting since {fmtDateTime(data.pending_po_at)}.
                    </p>
                  )}
                </div>
                {canFinance ? (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => advanceStage('Invoiced', 'PO received — project marked as Invoiced.')}
                    disabled={stageBusy != null}
                  >
                    {stageBusy === 'Invoiced' && <Loader2 className="h-4 w-4 animate-spin" />}
                    PO received → Mark Invoiced
                  </Button>
                ) : (
                  <div className="text-xs text-tcs-text-secondary">
                    Finance is following up with the client for their PO.
                  </div>
                )}
              </div>
            )}

            {/* AFB-0009 — Awaiting AM Input state. Amber callout with the
                finance note + bounce-back date; the responsible AM gets a
                resubmit affordance. */}
            {stage === 'Awaiting_AM_Input' && (
              <div className="mt-3 space-y-3">
                <div className="rounded-md border border-st-warn-border-strong bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
                  {/* The one label, read from the map rather than typed — a
                      literal here is how the third survivor got past a bundle
                      grep spelled with a capital I. */}
                  <div className="font-semibold uppercase tracking-wide">
                    {PROJECT_STAGE_LABELS.Awaiting_AM_Input} · finance sent this back
                  </div>
                  <p className="mt-1 whitespace-pre-wrap">
                    {data.awaiting_am_input_reason?.trim()
                      ? data.awaiting_am_input_reason
                      : 'Finance requested missing details on this card.'}
                  </p>
                  <p className="mt-1 text-[11px] text-st-warn-fg">
                    Sent back {fmtDateTime(data.awaiting_am_input_at)}.
                  </p>
                </div>
                {canResubmit ? (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleResubmit}
                    disabled={resubmitBusy || isDirty}
                  >
                    {resubmitBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Mark addressed → Ready to Invoice
                  </Button>
                ) : (
                  <div className="text-xs text-tcs-text-secondary">
                    Waiting on the account manager to add the missing details.
                  </div>
                )}
                {isDirty && canResubmit && (
                  <div className="text-xs text-st-warn-fg">
                    Save your pending edits before marking this addressed.
                  </div>
                )}
              </div>
            )}

            {stage === 'Invoiced' && canFinance && (
              <div className="mt-3 space-y-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => advanceStage('Completed', 'Project marked as Completed.')}
                  disabled={stageBusy != null}
                >
                  {stageBusy === 'Completed' && <Loader2 className="h-4 w-4 animate-spin" />}
                  Mark Completed
                </Button>
              </div>
            )}

            {stage === 'Completed' && (
              <div className="mt-3 text-xs text-tcs-text-secondary">
                Closed{data.invoiced_at ? ` · invoiced ${fmtDate(data.invoiced_at)}` : ''}.
              </div>
            )}

            {stageError && (
              <div className="mt-3">
                <ErrorState title="Couldn't advance the stage" body={stageError} />
              </div>
            )}
            {stageToast && (
              <div className="mt-3 rounded-md border border-st-ok-border bg-st-ok-bg px-3 py-2 text-xs text-st-ok-fg-strong">
                {stageToast}
              </div>
            )}
          </div>

          <div className="mt-4 text-xs text-tcs-text-muted">
            Created{' '}
            {data.creator?.employee_name_first
              ? `by ${data.creator.employee_name_first} ${data.creator.employee_name_last ?? ''}`
              : ''}{' '}
            on {fmtDate(data.created_date ?? data.created_at)}.
            {data.ready_to_invoice_at && (
              <> · Ready to invoice {fmtDate(data.ready_to_invoice_at)}.</>
            )}
            {data.invoiced_at && (
              <> · Invoiced {fmtDate(data.invoiced_at)}.</>
            )}
          </div>

          <div className="mt-6">
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>

          </div>

          <aside className="min-w-0 lg:sticky lg:top-4">
            <FinancePanel
              facilityName={data.facilities?.facility_name ?? data.facility_name ?? null}
              facilityId={data.facility_id ?? null}
              accountName={data.accounts?.account_name ?? data.account_name ?? null}
              bill={billAmtNum}
              paidOut={hasLines ? derivedAmounts.paid_out : (data.pay_amount ?? null)}
              chargedBack={derivedAmounts.charged_back}
              netCost={payAmtNum}
              marginAmount={data.margin_amount}
              marginPct={data.margin_pct}
              marginStatus={data.margin_status}
              workDone={readyCompletion}
              classification={
                isOffProject
                  ? [eventClassOption?.display_name, reasonOption?.display_name]
                      .filter(Boolean)
                      .join(' · ') || 'not ordinary billable work'
                  : null
              }
              stageLabel={stage ? PROJECT_STAGE_LABELS[stage] ?? stage : null}
              linesPaid={
                canReadPaymentRecords(role) && !linePaymentsError
                  ? summariseLinePayments(linePayments, payment?.payment_id ?? null, payment?.amount ?? null).linesPaid
                  : null
              }
            />
            {/* TCSD-0102 Phase 6A — the record of what finance did with this,
                under the panel that says what it is worth. C is the push; this
                is the look-back Ben asked for. */}
            {projectId && <FinanceHandoffStrip projectId={projectId} />}
          </aside>
          </div>

          {/* 2A + 2B — the two things an AM does to a record that already exists.
              Both available at ANY stage: 437 of 477 projects are Invoiced or
              Completed, so "after money moved" is the ordinary case here, not
              the edge one. ⛔ No role gate — TCSD-0170 settled that the audit
              trail is the control, and re-filing owes a REASON, not a
              permission. */}
          <div className="mt-6 flex flex-wrap items-start gap-x-6 gap-y-4 border-t border-tcs-border pt-4">
            <div>
              <button
                type="button"
                onClick={() => setHelperOpen('reclassify')}
                disabled={helperBusy}
                className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border-input bg-tcs-field px-3 py-1.5 text-xs font-semibold text-tcs-text hover:bg-tcs-surface disabled:opacity-50"
              >
                {helperBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />}
                Change what this is
              </button>
              <p className="mt-1 max-w-xs text-[11px] text-tcs-text-muted">
                Currently filed as <span className="text-tcs-text">{data.event_class ?? 'unfiled'}</span>.
                {isNotSent(stage)
                  ? ' Walks the same questions.'
                  : ' Finance has seen this one, so the change needs a reason in words. Nothing already billed or paid moves.'}
              </p>
            </div>
            <div>
            <button
              type="button"
              onClick={() => void handleRepeat()}
              disabled={repeatBusy}
              className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border-input bg-tcs-field px-3 py-1.5 text-xs font-semibold text-tcs-text hover:bg-tcs-surface disabled:opacity-50"
            >
              {repeatBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CopyPlus className="h-4 w-4" />}
              Repeat this one
            </button>
            <p className="mt-1 max-w-xs text-[11px] text-tcs-text-muted">
              Starts a new Draft with the same building, services, rates and sub.
              No dates &mdash; those belong to the new job. A chargeback line is never carried over.
            </p>
            </div>
          </div>

          {/* AFB-0022 #1 — delete a project entry (creator or admin only). */}
          {canDelete && (
            <div className="mt-6 border-t border-tcs-border pt-4">
              <button
                type="button"
                onClick={() => { setDeleteReason(''); setDeleteError(null); setDeleteOpen(true); }}
                className="inline-flex items-center gap-1.5 rounded-md border border-st-danger-border-strong bg-tcs-raised px-3 py-1.5 text-xs font-medium text-st-danger-fg hover:bg-st-danger-bg"
              >
                <Trash2 className="h-4 w-4" />
                Delete project
              </button>
              <p className="mt-1 text-[11px] text-tcs-text-muted">
                For entries created in error. Removes this project from the board; a snapshot is written to{' '}
                <code>deletion_requests</code> for audit.
              </p>
            </div>
          )}

          <ProjectActivityLog project={data} />

          {/* AFB-0009 — send-back reason modal. */}
          {sendBackOpen && (
            <div
              className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-4 sm:items-center"
              role="dialog"
              aria-modal="true"
              aria-label="Send project back to AM"
              onClick={() => { if (!sendBackBusy) setSendBackOpen(false); }}
            >
              <div
                className="w-full max-w-md rounded-lg border border-tcs-border bg-tcs-raised p-4 shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="text-sm font-semibold text-tcs-text">Send back to AM</h2>
                <p className="mt-1 text-xs text-tcs-text-secondary">
                  Tell the account manager what’s missing. The card moves to
                  “Not sent” and they’re notified next time they open Project Work.
                </p>
                <label className="mt-3 block">
                  <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                    Reason / missing data
                  </span>
                  <textarea
                    value={sendBackReason}
                    onChange={(e) => setSendBackReason(e.target.value.slice(0, 500))}
                    rows={4}
                    maxLength={500}
                    autoFocus
                    placeholder="e.g. Pay amount is blank — please confirm the sub’s rate for this job."
                    className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                  />
                  <span className="mt-1 block text-right text-[11px] text-tcs-text-muted">
                    {sendBackReason.length}/500
                  </span>
                </label>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setSendBackOpen(false)}
                    disabled={sendBackBusy}
                    className="text-sm text-tcs-text-secondary hover:text-tcs-text disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleSendBack}
                    disabled={sendBackBusy || sendBackReason.trim().length === 0}
                  >
                    {sendBackBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Send back
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* AFB-0022 #1 — delete confirm (reason required). */}
          {deleteOpen && (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center bg-tcs-scrim/60 p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Delete project"
              onClick={() => { if (!deleteBusy) setDeleteOpen(false); }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-xl border-2 border-st-danger-border-strong bg-tcs-overlay p-5 shadow-2xl"
              >
                <h3 className="text-base font-semibold text-st-danger-fg">Delete project</h3>
                <p className="mt-0.5 font-mono text-xs text-tcs-text-muted">{data.project_id}</p>
                {/* TCSD-0155 — a paid project CAN now be deleted, by finance or an
                    executive, and the payment record goes with it. The person
                    deciding has to see WHICH payment and HOW MUCH before they
                    can: that is what separates a misfired "sub paid" button from
                    a reconciled payment. Never summarise it as "a payment". */}
                {payment ? (
                  <div className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg p-3 text-xs text-st-danger-fg-strong">
                    <strong>
                      This also deletes the subcontractor payment record{' '}
                      {payment.payment_id}
                      {payment.amount != null ? `, ${fmtCurrency(payment.amount)}` : ''}.
                    </strong>{' '}
                    {/* ⛔ POSITIONS, never people — TCSD-0074. Same words the
                        database refusal uses, so the two cannot drift. */}
                    Only Finance or the COO can delete a project that has been
                    paid. Full snapshots of both records — with your reason — are
                    written to <code>deletion_requests</code> first. This removes our
                    record only: QuickBooks and the AP report are unaffected, and this
                    can&rsquo;t be undone here.
                  </div>
                ) : (
                  <div className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg p-3 text-xs text-st-danger-fg-strong">
                    <strong>Removes this project from the board.</strong> A snapshot is written to{' '}
                    <code>deletion_requests</code> for audit. This can&rsquo;t be undone here.
                  </div>
                )}
                <label className="mt-4 block">
                  <span className="text-xs font-medium text-tcs-text-secondary">
                    Reason <span className="text-st-danger-fg">*</span>
                  </span>
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value.slice(0, 500))}
                    rows={3}
                    maxLength={500}
                    autoFocus
                    placeholder="e.g. Duplicate entry — created twice by mistake."
                    className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-field p-2 text-sm shadow-inner focus:border-tcs-text focus:outline-none"
                  />
                </label>
                {deleteError && <p className="mt-2 text-xs text-st-danger-fg">{deleteError}</p>}
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteOpen(false)}
                    disabled={deleteBusy}
                    className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs font-medium text-tcs-text-secondary hover:bg-tcs-bg disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleteBusy || deleteReason.trim().length === 0}
                    className="inline-flex items-center gap-1.5 rounded-md bg-st-danger-solid px-3 py-1.5 text-xs font-medium text-st-solid-ink hover:bg-st-danger-solid disabled:opacity-50"
                  >
                    {deleteBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {payment ? 'Delete project and payment' : 'Delete project'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Run C Phase 2 — remove ONE service line, snapshotted. */}
          {removeLine && (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center bg-tcs-scrim/60 p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Remove service line"
              onClick={() => { if (!removeBusy) setRemoveLine(null); }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-xl border-2 border-st-danger-border-strong bg-tcs-overlay p-5 shadow-2xl"
              >
                <h3 className="text-base font-semibold text-st-danger-fg">Remove this service line</h3>
                <p className="mt-0.5 font-mono text-xs text-tcs-text-muted">
                  {removeLine.service_line_id || 'not saved yet'}
                </p>
                <div className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg p-3 text-xs text-st-danger-fg-strong">
                  {removeLine.service_line_id ? (
                    <>
                      <strong>Removes one service from this project.</strong> A snapshot of the whole
                      row is written to <code>deletion_requests</code> first, so it is recoverable.
                      The project&rsquo;s bill and pay are then recomputed from the lines that remain.
                    </>
                  ) : (
                    <>
                      <strong>This line has never been saved</strong>, so there is nothing to
                      snapshot — it is simply dropped from the form.
                    </>
                  )}
                </div>
                {removeLine.service_line_id && (
                  <label className="mt-4 block">
                    <span className="text-xs font-medium text-tcs-text-secondary">
                      Reason <span className="text-st-danger-fg">*</span>
                    </span>
                    <textarea
                      value={removeReason}
                      onChange={(e) => setRemoveReason(e.target.value.slice(0, 500))}
                      rows={3}
                      maxLength={500}
                      autoFocus
                      placeholder="e.g. Recorded twice — the carpet clean is already on line 1."
                      className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-field p-2 text-sm shadow-inner focus:border-tcs-text focus:outline-none"
                    />
                  </label>
                )}
                {removeError && <p className="mt-2 text-xs text-st-danger-fg">{removeError}</p>}
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setRemoveLine(null)}
                    disabled={removeBusy}
                    className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs font-medium text-tcs-text-secondary hover:bg-tcs-bg disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveLine}
                    disabled={
                      removeBusy ||
                      (!!removeLine.service_line_id && removeReason.trim().length === 0)
                    }
                    className="inline-flex items-center gap-1.5 rounded-md bg-st-danger-solid px-3 py-1.5 text-xs font-medium text-st-solid-ink hover:bg-st-danger-solid disabled:opacity-50"
                  >
                    {removeBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Remove line
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface LabeledInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'date' | 'number';
  /** A Home row opens the card at this field (item #650). */
  id?: string;
}
function LabeledInput({ label, value, onChange, type = 'text', id }: LabeledInputProps) {
  // Clear affordance for date inputs — native date pickers (esp. iOS/iPad) give
  // no reliable way to empty a field once set, so we expose an explicit ✕.
  const showClear = type === 'date' && value !== '';
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      <div className="relative">
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`block w-full scroll-mt-24 rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text ${showClear ? 'pr-9' : ''}`}
        />
        {showClear && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            onClick={() => onChange('')}
            className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </label>
  );
}

/**
 * Per-line payment. Run C Phase 4.
 *
 * Shows what paying THIS line would do — sub, amount, date — or, when it cannot
 * be paid, the reason, in the same words and the same gate order the RPC raises.
 * A person sees the whole set before paying any of it.
 */
function LinePaymentPanel({
  row,
  dirty,
  busy,
  error,
  onPay,
}: {
  row: ProjectLinePayment | undefined;
  dirty: boolean;
  busy: boolean;
  error: string | null;
  onPay: () => void;
}) {
  if (!row) return null;

  if (row.existing_payment_id) {
    return (
      <div className="mt-1 rounded-md border border-st-ok-border bg-st-ok-bg px-3 py-2 text-[11.5px] text-st-ok-fg">
        Paid · {row.existing_payment_id} · {fmtCurrency(row.amount)} to{' '}
        {row.provider_name ?? row.provider_id ?? 'a subcontractor'}
        {row.existing_payment_status ? ` · ${row.existing_payment_status}` : ''}
      </div>
    );
  }

  return (
    <div className="mt-1 rounded-md border border-tcs-border bg-tcs-surface/40 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11.5px] text-tcs-text-secondary">
          {row.mintable ? (
            <>
              Would pay {fmtCurrency(row.amount)} to{' '}
              <span className="text-tcs-text">{row.provider_name ?? row.provider_id}</span>, dated{' '}
              {fmtDate(row.pay_date)}
            </>
          ) : (
            <span className="text-tcs-text-muted">{row.blocked_reason ?? 'Not payable yet.'}</span>
          )}
        </span>
        {row.mintable && (
          <button
            type="button"
            onClick={onPay}
            disabled={busy || dirty}
            title={dirty ? 'Save your changes to this line first.' : undefined}
            className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Paying…' : 'Pay this line'}
          </button>
        )}
      </div>
      {/* An unsaved edit must not be paid: the RPC reads the STORED row, so the
          amount on screen and the amount paid would differ. */}
      {row.mintable && dirty && (
        <p className="mt-1 text-[11px] text-st-warn-fg">
          Unsaved changes on this line. Save first — this pays what is stored, not what is typed.
        </p>
      )}
      {row.special_pay && row.mintable && (
        <p className="mt-1 text-[11px] text-tcs-text-muted">
          This project is flagged, so paying it is a manual confirmation rather than a one-click action.
        </p>
      )}
      {error && <p className="mt-1 text-[11px] text-st-danger-fg">{error}</p>}
    </div>
  );
}

interface ReadOnlyFieldProps { label: string; value: React.ReactNode; }
function ReadOnlyField({ label, value }: ReadOnlyFieldProps) {
  return (
    <div className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      <div className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-sm text-tcs-text">{value ?? '—'}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project activity log — one chronological timeline (F4 + F5, 2026-06-02) that
// interleaves SYSTEM events (created, sent-back, ready, pending-PO, invoiced —
// derived from the projects-row timestamp/EID stamps) with the append-only
// human follow-up NOTES anyone can add ("called customer 1 Jun, received PO
// 2 Jun"). Notes are permanent: no UPDATE/DELETE policies exist on
// project_notes. Newest-first to match the rest of the app.
// ---------------------------------------------------------------------------
type TimelineItem =
  | { kind: 'note'; id: string; ts: string; actor: string; body: string }
  | { kind: 'event'; id: string; ts: string; actor: string | null; label: string };

function ProjectActivityLog({ project }: { project: ProjectRow }) {
  const projectId = project.project_id;
  const { employee } = useAuth();
  const { notes, isLoading, error, refetch } = useProjectNotes(projectId);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Resolve the EID stamps on the row to display names for the system events.
  const names = useEmployeeNames([
    project.created_by_eid,
    project.invoiced_by_eid,
    project.awaiting_am_input_by_eid,
    project.updated_by_eid,
  ]);
  const nameFor = (eid: string | null | undefined): string | null =>
    eid ? names[eid] ?? eid : null;

  async function handleAdd() {
    if (!employee?.employee_id || body.trim().length === 0) return;
    setBusy(true);
    setAddError(null);
    try {
      await addProjectNote(projectId, body.trim(), employee.employee_id);
      setBody('');
      refetch();
    } catch (err) {
      setAddError(errorMessage(err, 'Failed to add note.'));
    } finally {
      setBusy(false);
    }
  }

  function noteAuthor(n: (typeof notes)[number]): string {
    const a = n.author;
    if (a && (a.employee_name_first || a.employee_name_last)) {
      return `${a.employee_name_first ?? ''} ${a.employee_name_last ?? ''}`.trim();
    }
    return n.author_eid ?? 'Unknown';
  }

  // Synthesize system events from the row's stage-transition stamps. Only the
  // stamps that are present become events, so the timeline reflects the actual
  // lifecycle this project went through.
  const systemEvents = useMemo<TimelineItem[]>(() => {
    const ev: TimelineItem[] = [];
    const creatorName =
      project.creator?.employee_name_first || project.creator?.employee_name_last
        ? `${project.creator?.employee_name_first ?? ''} ${project.creator?.employee_name_last ?? ''}`.trim()
        : nameFor(project.created_by_eid);
    if (project.created_at) {
      ev.push({ kind: 'event', id: 'evt-created', ts: project.created_at, actor: creatorName, label: 'Project created' });
    }
    if (project.awaiting_am_input_at) {
      ev.push({
        kind: 'event',
        id: 'evt-sentback',
        ts: project.awaiting_am_input_at,
        actor: nameFor(project.awaiting_am_input_by_eid),
        label: project.awaiting_am_input_reason?.trim()
          ? `Sent back to AM for input — ${project.awaiting_am_input_reason.trim()}`
          : 'Sent back to AM for input',
      });
    }
    if (project.awaiting_am_input_resolved_at) {
      ev.push({ kind: 'event', id: 'evt-resolved', ts: project.awaiting_am_input_resolved_at, actor: null, label: 'AM marked input addressed → Ready to Invoice' });
    }
    if (project.ready_to_invoice_at) {
      ev.push({ kind: 'event', id: 'evt-ready', ts: project.ready_to_invoice_at, actor: null, label: 'Marked Ready to Invoice' });
    }
    if (project.pending_po_at) {
      ev.push({ kind: 'event', id: 'evt-pendingpo', ts: project.pending_po_at, actor: null, label: 'Sent to Pending PO — awaiting client PO number' });
    }
    if (project.invoiced_at) {
      ev.push({ kind: 'event', id: 'evt-invoiced', ts: project.invoiced_at, actor: nameFor(project.invoiced_by_eid), label: 'Marked Invoiced' });
    }
    return ev;
    // names drives actor resolution; recompute when it loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, names]);

  const timeline = useMemo<TimelineItem[]>(() => {
    const noteItems: TimelineItem[] = notes.map((n) => ({
      kind: 'note',
      id: n.note_id,
      ts: n.created_at,
      actor: noteAuthor(n),
      body: n.body,
    }));
    return [...systemEvents, ...noteItems].sort(
      (a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime(),
    );
  }, [systemEvents, notes]);

  return (
    <div className="mt-6 rounded-lg border border-tcs-border bg-tcs-raised p-4">
      <h3 className="text-sm font-semibold text-tcs-text">Activity log</h3>
      <p className="mt-0.5 text-xs text-tcs-text-secondary">
        Stage changes and follow-ups in one timeline. Add a note to log client/sub
        contact — entries are permanent and attributed to you.
      </p>

      <div className="mt-3">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 1000))}
          rows={2}
          placeholder="e.g. Emailed customer AP for PO — 2 Jun. Followed up by phone, PO promised this week."
          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
        />
        <div className="mt-1 flex items-center justify-between">
          <span className="text-[11px] text-tcs-text-muted">{body.length}/1000</span>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleAdd}
            disabled={busy || body.trim().length === 0 || !employee?.employee_id}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Add note
          </Button>
        </div>
        {addError && (
          <div className="mt-2 text-xs text-st-danger-fg">{addError}</div>
        )}
      </div>

      <div className="mt-4">
        {error && <div className="text-xs text-st-danger-fg">Couldn’t load the notes: {error}</div>}
        {isLoading && (
          <div className="mb-2 text-[11px] text-tcs-text-muted">Loading notes…</div>
        )}
        {timeline.length === 0 ? (
          <div className="text-xs text-tcs-text-muted">No activity yet.</div>
        ) : (
          <ul className="space-y-3">
            {timeline.map((item) =>
              item.kind === 'note' ? (
                <li key={item.id} className="border-l-2 border-tcs-border pl-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium text-tcs-text">{item.actor}</span>
                    <span className="text-[11px] text-tcs-text-muted">{fmtDateTime(item.ts)}</span>
                  </div>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-tcs-text-secondary">{item.body}</p>
                </li>
              ) : (
                <li key={item.id} className="border-l-2 border-tcs-border-strong pl-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium text-tcs-text-secondary">
                      {item.label}
                      {item.actor ? <span className="font-normal text-tcs-text-muted"> · {item.actor}</span> : null}
                    </span>
                    <span className="text-[11px] text-tcs-text-muted">{fmtDateTime(item.ts)}</span>
                  </div>
                </li>
              ),
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
